import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Trophy, 
  Camera, 
  DollarSign, 
  Calendar, 
  Users, 
  ChevronRight,
  Check,
  Target,
  TrendingUp,
  Globe,
  Clock,
  ArrowRight,
  Heart,
  Award,
  Megaphone,
  BarChart3,
  Handshake,
  Sparkles,
  Shield,
  Building2,
  CircleDollarSign,
  UserCheck,
  Zap,
  User,
  Mail,
  Phone,
  ChevronLeft,
  Loader2,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = ['Basic Info', 'Organization', 'Event Details', 'Final'];

const ORG_TYPES = [
  'Nonprofit / Charity',
  'Corporate Foundation',
  'Community Organization',
  'Church / Faith-Based',
  'School / University',
  'Other'
];

const EVENT_SIZES = ['Under 50', '50–100', '100–200', '200+'];

const EVENT_EXPERIENCE = [
  'First-time organizer',
  'Hosted 1–2 events',
  'Hosted 3–5 events',
  'Experienced (5+ events)'
];

const INTEREST_OPTIONS = [
  'Live scoring & leaderboards',
  'Sponsor management',
  'Player registration',
  'Fundraising tracking',
  'Auction / raffle tools',
  'Tournament payouts',
  'Bundle member access (10% discount)'
];

// Mirrors backend BUSINESS_CATEGORIES — used by the optional Business Network section
const BUSINESS_CATEGORIES = [
  'Real Estate', 'Finance & Investing', 'Legal', 'Insurance', 'Healthcare & Medical',
  'Construction & Trades', 'Tech & Software', 'Hospitality & Restaurants', 'Automotive',
  'Marketing & Media', 'Consulting', 'Retail & E-commerce', 'Education & Coaching',
  'Fitness & Wellness', 'Golf Industry', 'Other'
];

export default function FundraiserLanding() {
  const [isVisible, setIsVisible] = useState({});
  const [formStep, setFormStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    organization_name: '',
    org_type: '',
    event_size: '',
    event_experience: '',
    interests: [],
    fundraising_goal: '',
    additional_info: '',
    // Business Network fields (optional — published to /network if business_name + description filled)
    business_category: '',
    business_name: '',
    business_description: '',
    business_website: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const validateStep = () => {
    if (formStep === 0) {
      if (!formData.full_name.trim()) return 'Full name is required';
      if (!formData.email.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    }
    return '';
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { setFormError(err); return; }
    setFormError('');
    setFormStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setFormError('');
    setFormStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      const payload = JSON.stringify({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        organizer_type: formData.org_type,
        network_size: formData.event_size,
        event_frequency: formData.event_experience,
        interests: formData.interests,
        events_per_year: parseInt(formData.fundraising_goal) || 0,
        additional_info: `Org: ${formData.organization_name}\nGoal: $${formData.fundraising_goal}\n${formData.additional_info}`.trim(),
        // Business Network fields
        business_category: formData.business_category,
        business_name: formData.business_name,
        business_description: formData.business_description,
        business_website: formData.business_website,
      });
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/applications/admin`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          let data = {};
          try { data = JSON.parse(xhr.responseText); } catch {}
          if (xhr.status >= 200 && xhr.status < 300) resolve(data);
          else if (xhr.status === 409) reject(new Error('An application with this email already exists'));
          else reject(new Error(data.detail || 'Submission failed'));
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(payload);
      });
      setSubmitted(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="https://thebundlegolfclubusa.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="nav-main-site-link">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-gray-900 tracking-tight">DMV GOLF CONSORTIUM</span>
                <span className="text-emerald-600 text-xs block -mt-0.5">+ The Bundle Golf Club</span>
              </div>
            </a>
            <Button 
              onClick={scrollToContact}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 shadow-lg shadow-emerald-600/20"
            >
              Start Your Fundraiser
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/videos/fundraiser-promo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f0d]/50 via-transparent to-black/40" />
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div>
              {/* Trust Badge */}
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
                data-animate
                id="hero-badge"
              >
                <Heart className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300 font-medium">Trusted by Charity Organizers</span>
              </div>

              {/* Headline */}
              <h1 
                className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6 text-white transition-all duration-1000 ${isVisible['hero-badge'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                Run a Better Golf Fundraiser.
                <span className="block text-emerald-400">Raise More Money.</span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-gray-300 max-w-xl mb-10 leading-relaxed">
                Everything you need to organize, grow, and elevate your charity golf tournament — in one complete system.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToContact}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg px-8 py-6 rounded-xl shadow-xl shadow-emerald-500/30 group"
                >
                  Start Your Fundraiser
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl"
                >
                  See How It Works
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/20">
                {[
                  { icon: <Shield className="w-5 h-5" />, label: 'Secure & Reliable' },
                  { icon: <Zap className="w-5 h-5" />, label: 'Easy Setup' },
                  { icon: <Users className="w-5 h-5" />, label: '500+ Golfers Network' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-400">
                    <span className="text-emerald-400">{item.icon}</span>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">See It In Action</span>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">Your Fundraiser Dashboard</h2>
          </div>
          <div className="relative max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Annual Charity Tournament</span>
                  <h3 className="text-xl font-bold text-gray-900">Hope Foundation Classic</h3>
                </div>
                <div className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">LIVE</div>
              </div>
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Raised</span>
                  <span className="font-bold text-emerald-600">$47,500 / $50,000</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full w-[95%] bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { value: '72', label: 'Players' },
                  { value: '12', label: 'Sponsors' },
                  { value: '18', label: 'Teams' }
                ].map((stat, i) => (
                  <div key={i} className="text-center p-3 bg-gray-50 rounded-xl">
                    <div className="text-2xl font-black text-gray-900">{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Top Teams</div>
                {['Team Johnson & Associates', 'First National Bank', 'Smith Family Foundation'].map((team, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-400 text-yellow-900' : 
                        i === 1 ? 'bg-gray-300 text-gray-700' : 
                        'bg-orange-300 text-orange-800'
                      }`}>{i + 1}</span>
                      <span className="text-sm font-medium text-gray-700">{team}</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-600">-{8 - i}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-3 border border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">vs Last Year</div>
                  <div className="text-sm font-bold text-emerald-600">+23% Raised</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 border border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Registration</div>
                  <div className="text-sm font-bold text-blue-600">Sold Out!</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: The Problem */}
      <section className="py-24 bg-gray-50" id="problem" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">The Challenge</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6 text-gray-900">
              Fundraising Golf Events Are Hard to Manage
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We understand the challenges you face. That's why we built something better.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: '📋', title: 'Manual Player Tracking', desc: 'Spreadsheets, emails, and confusion' },
              { icon: '📝', title: 'Scorecard Chaos', desc: 'Slow scoring and calculation errors' },
              { icon: '⏰', title: 'Tee Time Coordination', desc: 'Scheduling headaches and no-shows' },
              { icon: '📉', title: 'Limited Turnout', desc: 'Struggling to fill spots every year' },
              { icon: '🤝', title: 'Sponsor Challenges', desc: 'Difficulty attracting and retaining sponsors' },
              { icon: '💸', title: 'Payout Disputes', desc: 'Contest winnings cause confusion' }
            ].map((item, i) => (
              <div 
                key={i}
                className={`flex items-start gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-emerald-200 hover:shadow-md transition-all ${isVisible['problem'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 50}ms`, transition: 'all 0.5s ease-out' }}
              >
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: The Solution */}
      <section className="py-24 relative overflow-hidden" id="solution" data-animate>
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-teal-700" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <span className="text-emerald-200 text-sm font-bold uppercase tracking-widest">The Solution</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-4 mb-6">
              A Complete System for Golf Fundraisers
            </h2>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto mb-12">
              This platform simplifies every part of running a golf fundraiser — from player management 
              to scoring to event organization. Focus on your cause, not logistics.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { icon: <Sparkles className="w-8 h-8" />, value: 'Simplified', label: 'Event Management' },
                { icon: <TrendingUp className="w-8 h-8" />, value: 'Increased', label: 'Participation' },
                { icon: <CircleDollarSign className="w-8 h-8" />, value: 'More', label: 'Money Raised' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                  <div className="text-2xl font-black">{item.value}</div>
                  <div className="text-emerald-200">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Powerful Event Tools */}
      <section className="py-24" id="features" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Platform Features</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6 text-gray-900">
              Powerful Event Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to run a professional, high-impact golf fundraiser.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <BarChart3 className="w-7 h-7" />,
                title: 'Live Leaderboard',
                benefit: 'Real-time scoring keeps players engaged',
                bullets: ['Adds excitement and competition', 'Updates automatically', 'Shareable for sponsors'],
                color: 'emerald'
              },
              {
                icon: <Camera className="w-7 h-7" />,
                title: 'Scorecard Scanning',
                benefit: 'Snap a photo → scores entered instantly',
                bullets: ['AI-powered OCR', 'Reduces errors', 'Saves volunteer time'],
                color: 'blue'
              },
              {
                icon: <Trophy className="w-7 h-7" />,
                title: 'Automated Results',
                benefit: 'Fast, accurate results — no confusion',
                bullets: ['Instant calculations', 'Contest winners auto-determined', 'Print-ready reports'],
                color: 'purple'
              },
              {
                icon: <Calendar className="w-7 h-7" />,
                title: 'Tournament Management',
                benefit: 'Organize tee times and players',
                bullets: ['Drag-and-drop scheduling', 'Player notifications', 'One centralized system'],
                color: 'orange'
              },
              {
                icon: <Megaphone className="w-7 h-7" />,
                title: 'Player Reach & Exposure',
                benefit: 'Access a broader network of golfers',
                bullets: ['Increase event participation', 'Reach new donors', 'Build your email list'],
                color: 'rose'
              },
              {
                icon: <Building2 className="w-7 h-7" />,
                title: 'Sponsor Management',
                benefit: 'Professional sponsor experience',
                bullets: ['Branded leaderboards', 'Sponsor visibility tools', 'ROI tracking'],
                color: 'teal'
              }
            ].map((feature, i) => {
              const colorClasses = {
                emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
                blue: 'bg-blue-100 text-blue-600 border-blue-200',
                purple: 'bg-purple-100 text-purple-600 border-purple-200',
                orange: 'bg-orange-100 text-orange-600 border-orange-200',
                rose: 'bg-rose-100 text-rose-600 border-rose-200',
                teal: 'bg-teal-100 text-teal-600 border-teal-200'
              };
              
              return (
                <Card 
                  key={i}
                  className={`bg-white border border-gray-200 hover:shadow-xl hover:border-emerald-200 transition-all duration-300 ${isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl ${colorClasses[feature.color]} flex items-center justify-center mb-5`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-emerald-600 font-medium mb-4">{feature.benefit}</p>
                    <ul className="space-y-2">
                      {feature.bullets.map((bullet, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Raise More Money */}
      <section className="py-24 bg-gray-50" id="raise-more" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">The Impact</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6 text-gray-900">
                More Players. More Sponsors. <span className="text-emerald-600">More Impact.</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                When your event runs smoothly and looks professional, everyone wins — 
                especially the cause you're supporting.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Increase turnout', desc: 'Better visibility attracts more players' },
                  { title: 'Impress sponsors', desc: 'Professional experience builds confidence' },
                  { title: 'Scale your events', desc: 'Run larger, more organized tournaments' },
                  { title: 'Build loyalty', desc: 'Players and sponsors return year after year' }
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200 ${isVisible['raise-more'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${i * 100}ms`, transition: 'all 0.5s ease-out' }}
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Year-Over-Year Growth</h3>
                
                <div className="space-y-6">
                  {[
                    { label: 'Funds Raised', before: '$32,000', after: '$47,500', increase: '+48%' },
                    { label: 'Player Registration', before: '48', after: '72', increase: '+50%' },
                    { label: 'Sponsors', before: '6', after: '12', increase: '+100%' }
                  ].map((stat, i) => (
                    <div key={i}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600">{stat.label}</span>
                        <span className="text-sm font-bold text-emerald-600">{stat.increase}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="text-xs text-gray-400 mb-1">Before</div>
                          <div className="text-lg font-bold text-gray-400">{stat.before}</div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-emerald-500" />
                        <div className="flex-1">
                          <div className="text-xs text-gray-400 mb-1">After</div>
                          <div className="text-lg font-bold text-emerald-600">{stat.after}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute -z-10 top-4 left-4 right-4 bottom-4 bg-emerald-100 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Sponsorship + Exposure */}
      <section className="py-24" id="sponsors" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Sponsor Success</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6 text-gray-900">
              Attract and Retain Sponsors
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Give your sponsors a professional experience that makes them want to come back.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Award className="w-8 h-8" />, title: 'Professional Presentation', desc: 'Events that look and feel premium' },
              { icon: <UserCheck className="w-8 h-8" />, title: 'Better Organization', desc: 'Builds sponsor confidence' },
              { icon: <Globe className="w-8 h-8" />, title: 'Branding Opportunities', desc: 'Visibility on leaderboards & more' },
              { icon: <Handshake className="w-8 h-8" />, title: 'Network Access', desc: 'Connect with a growing golf community' }
            ].map((item, i) => (
              <div 
                key={i}
                className={`text-center p-6 bg-gray-50 rounded-2xl hover:bg-emerald-50 transition-colors ${isVisible['sponsors'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 100}ms`, transition: 'all 0.5s ease-out' }}
              >
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-4 text-emerald-600">
                  {item.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Community + Network */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white" id="network" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest">DMV Golf Network</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6">
                Tap Into the DMV Golf Network
              </h2>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                You're not just getting software — you're joining a community of organizers 
                who share knowledge, resources, and opportunities.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Connect with other organizers',
                  'Share best practices',
                  'Collaborate on larger events',
                  'Build long-term relationships'
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-3 p-4 bg-white/5 rounded-xl ${isVisible['network'] ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: `${i * 100}ms`, transition: 'opacity 0.5s ease-out' }}
                  >
                    <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Network Visual */}
            <div className="relative h-80 hidden lg:flex items-center justify-center">
              <div className="relative w-full h-full flex items-center justify-center">
                {/* Center */}
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/30 z-10">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                
                {/* Orbiting nodes */}
                {['Charities', 'Sponsors', 'Golfers', 'Organizers'].map((label, i) => {
                  const angle = (i * 90 - 45) * (Math.PI / 180);
                  const radius = 120;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  return (
                    <div
                      key={i}
                      className="absolute bg-white/10 backdrop-blur rounded-xl px-4 py-2 border border-white/20"
                      style={{ 
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                        left: '50%',
                        top: '50%'
                      }}
                    >
                      <span className="text-sm font-medium text-white">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: What This Means For You */}
      <section className="py-24" id="benefits" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Your Benefits</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-4 text-gray-900">
              What This Means For You
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              'Less time managing logistics',
              'More time focusing on your cause',
              'Higher turnout and engagement',
              'More professional events',
              'Stronger sponsor relationships',
              'Greater fundraising impact'
            ].map((item, i) => (
              <div 
                key={i}
                className={`flex items-center gap-4 p-5 rounded-xl bg-emerald-50 border border-emerald-100 ${isVisible['benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 50}ms`, transition: 'all 0.5s ease-out' }}
              >
                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-gray-800">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 8: Social Proof / Vision */}
      <section className="py-24 bg-gray-50" id="vision" data-animate>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 border border-emerald-200 mb-8">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-emerald-700 font-medium">Imagine This</span>
          </div>
          
          <div className={`text-2xl sm:text-3xl lg:text-4xl leading-relaxed ${isVisible['vision'] ? 'opacity-100' : 'opacity-0'}`} style={{ transition: 'opacity 1s ease-out' }}>
            <p className="text-gray-500 mb-6">
              "Your tournament day runs <span className="text-gray-900 font-bold">smoothly</span>.
            </p>
            <p className="text-gray-500 mb-6">
              Players are <span className="text-emerald-600 font-bold">engaged</span>. 
              Sponsors are <span className="text-emerald-600 font-bold">visible</span>.
            </p>
            <p className="text-gray-500 mb-6">
              Scores update <span className="text-gray-900 font-bold">live</span>.
            </p>
            <p className="text-gray-900 font-bold">
              And your event raises more than ever before."
            </p>
          </div>
        </div>
      </section>

      {/* Section 9: Simple Setup */}
      <section className="py-24" id="setup" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Quick Start Process</h3>
                
                {[
                  { step: '1', title: 'Tell Us About Your Event', desc: 'Share your tournament details', time: 'Day 1' },
                  { step: '2', title: 'We Set Up Your System', desc: 'Customized for your needs', time: 'Day 2-3' },
                  { step: '3', title: 'Invite Your Players', desc: 'Easy registration links', time: 'Day 4-5' },
                  { step: '4', title: 'Run Your Event', desc: 'We support you all the way', time: 'Event Day' }
                ].map((item, i) => (
                  <div 
                    key={i}
                    className={`flex items-start gap-4 p-4 rounded-xl mb-3 last:mb-0 ${i % 2 === 0 ? 'bg-gray-50' : ''} ${isVisible['setup'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${i * 100}ms`, transition: 'all 0.5s ease-out' }}
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center font-bold text-white flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-gray-900">{item.title}</h4>
                        <span className="text-xs text-emerald-600 font-medium">{item.time}</span>
                      </div>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Easy Onboarding</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-4 mb-6 text-gray-900">
                Launch Your Fundraiser <span className="text-emerald-600">with Ease</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                No technical experience required. We guide you through every step and ensure 
                your event is set up for success.
              </p>
              
              <div className="flex flex-wrap gap-4">
                {[
                  { icon: <Clock className="w-5 h-5" />, text: 'Quick Setup' },
                  { icon: <Shield className="w-5 h-5" />, text: 'Full Support' },
                  { icon: <Zap className="w-5 h-5" />, text: 'No Tech Skills Needed' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full text-emerald-700">
                    {item.icon}
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10: Application Form */}
      <section className="py-24 relative overflow-hidden" id="contact-section">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            
            {/* Left: Benefits */}
            <div className="lg:col-span-2 text-white">
              <Heart className="w-12 h-12 text-emerald-200 mb-6" />
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Start Your Fundraiser
              </h2>
              <p className="text-emerald-100 mb-8 leading-relaxed">
                Join the community of charity organizers raising more money with better golf events.
              </p>
              <div className="space-y-4">
                {[
                  'Live scoring & leaderboards',
                  'Sponsor management tools',
                  'Player registration system',
                  'Fundraising goal tracking',
                  'Full event support team'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-200" />
                    </div>
                    <span className="text-emerald-50">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
                
                {submitted ? (
                  <div className="p-8 sm:p-12 text-center" data-testid="fundraiser-form-success">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-3">You're All Set!</h3>
                    <p className="text-gray-600 max-w-sm mx-auto">
                      Our team will review your application and reach out within <span className="text-emerald-600 font-medium">24–48 hours</span> to help you get started.
                    </p>
                    <div className="mt-8 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                      <p className="text-sm text-emerald-700">See what other organizers are building:</p>
                      <a href="https://thebundlegolfclubusa.com" className="text-emerald-600 font-semibold text-sm hover:underline mt-1 inline-block">Visit The Bundle Golf Club →</a>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Form Header */}
                    <div className="px-6 sm:px-8 pt-8 pb-4">
                      <h3 className="text-2xl font-black text-gray-900 mb-1" data-testid="fundraiser-form-title">Start Your Fundraiser</h3>
                      <p className="text-gray-500 text-sm">Step {formStep + 1} of {STEP_LABELS.length} — {STEP_LABELS[formStep]}</p>
                      
                      {/* Progress Bar */}
                      <div className="flex gap-2 mt-4" data-testid="fundraiser-form-progress">
                        {STEP_LABELS.map((_, i) => (
                          <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                i < formStep ? 'bg-emerald-500 w-full' : 
                                i === formStep ? 'bg-emerald-500 w-1/2' : 'w-0'
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Form Body */}
                    <div className="px-6 sm:px-8 py-6 min-h-[320px]">
                      {formError && (
                        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="fundraiser-form-error">
                          {formError}
                        </div>
                      )}

                      {/* Step 1: Basic Info */}
                      {formStep === 0 && (
                        <div className="space-y-5" data-testid="fundraiser-step-1">
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Full Name <span className="text-emerald-500">*</span></label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input data-testid="fundraiser-input-name" type="text" value={formData.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Your full name" className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Email <span className="text-emerald-500">*</span></label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input data-testid="fundraiser-input-email" type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="you@organization.com" className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Phone Number</label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input data-testid="fundraiser-input-phone" type="tel" value={formData.phone} onChange={e => updateField('phone', e.target.value)} placeholder="(555) 123-4567" className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-sm text-gray-600 mb-1.5 block">City</label>
                              <input data-testid="fundraiser-input-city" type="text" value={formData.city} onChange={e => updateField('city', e.target.value)} placeholder="City" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                            </div>
                            <div>
                              <label className="text-sm text-gray-600 mb-1.5 block">State</label>
                              <input data-testid="fundraiser-input-state" type="text" maxLength={2} value={formData.state} onChange={e => updateField('state', e.target.value.toUpperCase())} placeholder="MD" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all uppercase" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 2: Organization */}
                      {formStep === 1 && (
                        <div className="space-y-6" data-testid="fundraiser-step-2">
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Organization Name</label>
                            <input data-testid="fundraiser-input-org" type="text" value={formData.organization_name} onChange={e => updateField('organization_name', e.target.value)} placeholder="Your organization or charity" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-3 block">Organization Type</label>
                            <div className="grid grid-cols-2 gap-2">
                              {ORG_TYPES.map(type => (
                                <button key={type} onClick={() => updateField('org_type', type)} className={`px-4 py-3 rounded-xl border text-left text-sm transition-all ${formData.org_type === type ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'}`}>
                                  {formData.org_type === type && <Check className="w-3.5 h-3.5 inline mr-2" />}{type}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-3 block">Expected Event Size</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {EVENT_SIZES.map(size => (
                                <button key={size} onClick={() => updateField('event_size', size)} className={`px-4 py-3 rounded-xl border text-center text-sm transition-all ${formData.event_size === size ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'}`}>
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Event Details */}
                      {formStep === 2 && (
                        <div className="space-y-6" data-testid="fundraiser-step-3">
                          <div>
                            <label className="text-sm text-gray-600 mb-3 block">Your experience level</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {EVENT_EXPERIENCE.map(exp => (
                                <button key={exp} onClick={() => updateField('event_experience', exp)} className={`px-4 py-3 rounded-xl border text-left text-sm transition-all ${formData.event_experience === exp ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'}`}>
                                  {formData.event_experience === exp && <Check className="w-3.5 h-3.5 inline mr-2" />}{exp}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-3 block">What features interest you most? <span className="text-gray-400">(select all)</span></label>
                            <div className="space-y-2">
                              {INTEREST_OPTIONS.map(interest => (
                                <button key={interest} onClick={() => toggleInterest(interest)} className={`w-full px-4 py-3 rounded-xl border text-left text-sm flex items-center gap-3 transition-all ${formData.interests.includes(interest) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'}`}>
                                  <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${formData.interests.includes(interest) ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                    {formData.interests.includes(interest) && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  {interest}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 4: Final */}
                      {formStep === 3 && (
                        <div className="space-y-5" data-testid="fundraiser-step-4">
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Fundraising Goal ($)</label>
                            <input data-testid="fundraiser-input-goal" type="text" value={formData.fundraising_goal} onChange={e => updateField('fundraising_goal', e.target.value)} placeholder="e.g. 25000" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1.5 block">Anything else? <span className="text-gray-400">(optional)</span></label>
                            <textarea data-testid="fundraiser-input-info" value={formData.additional_info} onChange={e => updateField('additional_info', e.target.value)} placeholder="Tell us about your event, cause, or any specific needs..." rows={4} className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all resize-none" />
                          </div>

                          {/* ===== Business Network (optional) ===== */}
                          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <div className="flex items-start gap-3 mb-3">
                              <Building2 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-black text-amber-900">List on the Business Network <span className="text-amber-700 font-semibold">(optional)</span></h4>
                                <p className="text-xs text-amber-800 mt-0.5">Members search the Bundle Business Network to refer trusted golf connections. Add your business so they can find &amp; refer you.</p>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-amber-900 mb-1 block">Industry / Category</label>
                                <select data-testid="fundraiser-input-biz-category" value={formData.business_category} onChange={e => updateField('business_category', e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-white border border-amber-300 text-gray-900 text-sm focus:outline-none focus:border-amber-500">
                                  <option value="">Select a category</option>
                                  {BUSINESS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-amber-900 mb-1 block">Business Name</label>
                                <input data-testid="fundraiser-input-biz-name" type="text" maxLength={120} value={formData.business_name} onChange={e => updateField('business_name', e.target.value)} placeholder="Smith & Co. Realty" className="w-full px-3 py-2.5 rounded-lg bg-white border border-amber-300 text-gray-900 text-sm focus:outline-none focus:border-amber-500" />
                              </div>
                              <div>
                                <label className="text-xs text-amber-900 mb-1 block">Short Description</label>
                                <textarea data-testid="fundraiser-input-biz-desc" rows={2} maxLength={250} value={formData.business_description} onChange={e => updateField('business_description', e.target.value.slice(0,250))} placeholder="What you do, how you help fellow golfers (max 250 chars)…" className="w-full px-3 py-2.5 rounded-lg bg-white border border-amber-300 text-gray-900 text-sm focus:outline-none focus:border-amber-500 resize-none" />
                                <p className="text-[10px] text-amber-700 mt-0.5 text-right">{formData.business_description.length}/250</p>
                              </div>
                              <div>
                                <label className="text-xs text-amber-900 mb-1 block">Website <span className="text-amber-700">(optional)</span></label>
                                <input data-testid="fundraiser-input-biz-website" type="text" maxLength={200} value={formData.business_website} onChange={e => updateField('business_website', e.target.value)} placeholder="https://yourwebsite.com" className="w-full px-3 py-2.5 rounded-lg bg-white border border-amber-300 text-gray-900 text-sm focus:outline-none focus:border-amber-500" />
                              </div>
                            </div>
                          </div>

                          <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                            <h4 className="text-sm font-semibold text-emerald-600 mb-3">Application Summary</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between"><span className="text-gray-400">Name</span><span className="text-gray-900">{formData.full_name}</span></div>
                              <div className="flex justify-between"><span className="text-gray-400">Email</span><span className="text-gray-900">{formData.email}</span></div>
                              {formData.organization_name && <div className="flex justify-between"><span className="text-gray-400">Organization</span><span className="text-gray-900">{formData.organization_name}</span></div>}
                              {formData.org_type && <div className="flex justify-between"><span className="text-gray-400">Type</span><span className="text-gray-900">{formData.org_type}</span></div>}
                              {formData.event_size && <div className="flex justify-between"><span className="text-gray-400">Event Size</span><span className="text-gray-900">{formData.event_size} players</span></div>}
                              {formData.interests.length > 0 && <div className="flex justify-between"><span className="text-gray-400">Interests</span><span className="text-gray-900">{formData.interests.length} selected</span></div>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Form Footer */}
                    <div className="px-6 sm:px-8 pb-8 flex items-center justify-between gap-3">
                      {formStep > 0 ? (
                        <Button data-testid="fundraiser-back-btn" variant="ghost" onClick={prevStep} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                          <ChevronLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                      ) : <div />}

                      {formStep < 3 ? (
                        <Button data-testid="fundraiser-next-btn" onClick={nextStep} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-xl">
                          Continue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      ) : (
                        <Button data-testid="fundraiser-submit-btn" onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-xl disabled:opacity-50">
                          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>) : (<>Submit Application<ArrowRight className="w-4 h-4 ml-2" /></>)}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <p className="text-center text-xs text-emerald-200/60 mt-4">
                Built for organizers who want to <span className="text-white/80 font-semibold">raise more</span> and <span className="text-white/80 font-semibold">stress less</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="https://thebundlegolfclubusa.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="footer-main-site-link">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold">DMV Golf Consortium + The Bundle Golf Club</span>
            </a>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="https://thebundlegolfclubusa.com" className="hover:text-white transition-colors" data-testid="footer-visit-site-link">Visit Main Site</a>
              <a href="/become-admin" className="hover:text-white transition-colors">Become a Director</a>
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500 mt-8">
            © 2026 DMV Golf Consortium. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
